/**
 * Results Feedback Panel (Beta)
 *
 * Collects user feedback on the results interface for continuous improvement.
 * Implements "Idea 1 - Quick Beta Feedback" from the wireframe.
 *
 * Gated by: NEW_DESIGN_BETA flag
 * Rollback: Remove from ResultsSidebar.tsx
 */

import React, { useState } from 'react';
import { logger } from '../../../lib/logging';
import { useAuth } from '../../../contexts/AuthContext';

export interface ResultsFeedbackPanelProps {
  comparisonId?: string | null;
  modelSettings?: Record<string, unknown>;
  correlationId?: string;
  onSuccess?: () => void;
}

type FeedbackCategory = 'bug' | 'feature' | 'idea' | 'other';

interface SubmitState {
  loading: boolean;
  success: boolean;
  error: string | null;
}

export const ResultsFeedbackPanel: React.FC<ResultsFeedbackPanelProps> = ({
  comparisonId,
  modelSettings,
  correlationId,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [category, setCategory] = useState<FeedbackCategory>('bug');
  const [details, setDetails] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>({
    loading: false,
    success: false,
    error: null,
  });

  const getButtonLabel = () => {
    switch (category) {
      case 'bug':
        return 'Report bug';
      case 'feature':
        return 'Send request';
      default:
        return 'Send feedback';
    }
  };

  const getCategoryLabel = (cat: FeedbackCategory) => {
    switch (cat) {
      case 'bug':
        return 'Bug';
      case 'feature':
        return 'Feature request';
      case 'idea':
        return 'Idea';
      case 'other':
        return 'Other';
    }
  };

  const handleSubmit = async () => {
    if (!details.trim()) {
      setSubmitState({
        loading: false,
        success: false,
        error: 'Please enter some details',
      });
      return;
    }

    setSubmitState({ loading: true, success: false, error: null });

    const startTime = performance.now();

    try {
      // Get Firebase ID token if user is logged in
      const idToken = user ? await user.getIdToken() : null;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }

      const response = await fetch('/api/results/feedback', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          category,
          details: details.trim(),
          pageUrl: window.location.href,
          comparisonId: comparisonId || null,
          modelSettings: modelSettings || null,
          userAgent: navigator.userAgent,
          clientInfo: {
            viewport: {
              width: window.innerWidth,
              height: window.innerHeight,
            },
            screen: {
              width: window.screen.width,
              height: window.screen.height,
            },
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit feedback');
      }

      const result = await response.json();

      // Log success
      logger.info('feedback_submit_success', {
        op: 'feedback_submit',
        component: 'ResultsFeedbackPanel',
        category,
        comparisonId: comparisonId || 'none',
        correlationId: result.correlationId || correlationId,
        duration_ms: performance.now() - startTime,
        success: true,
      });

      // Show success state
      setSubmitState({ loading: false, success: true, error: null });

      // Clear details and notify parent after short delay
      setTimeout(() => {
        setDetails('');
        setSubmitState({ loading: false, success: false, error: null });
        onSuccess?.();
      }, 2000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      logger.error('feedback_submit_error', {
        op: 'feedback_submit',
        component: 'ResultsFeedbackPanel',
        category,
        comparisonId: comparisonId || 'none',
        correlationId,
        duration_ms: performance.now() - startTime,
        success: false,
        err_message: errorMessage,
      });

      setSubmitState({
        loading: false,
        success: false,
        error: errorMessage,
      });
    }
  };

  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-700/60 bg-slate-900/70 shadow-lg">
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "url('/images/Beta-back.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          imageRendering: 'crisp-edges',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-fuchsia-500/5 to-amber-400/5 pointer-events-none" />

      <div className="relative space-y-3 p-4 sm:p-5 backdrop-blur-[2px]">
        {/* Category Pills */}
        <div className="space-y-2">
          <label className="text-xs text-slate-200 block drop-shadow-sm bg-slate-900/40 px-2 py-0.5 rounded w-fit">
            Category
          </label>
          <div className="flex flex-wrap gap-2">
            {(['bug', 'feature', 'idea', 'other'] as FeedbackCategory[]).map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                disabled={submitState.loading}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                  category === cat
                    ? 'bg-cyan-500/40 border border-cyan-300/80 text-white shadow-sm shadow-cyan-500/40'
                    : 'bg-slate-800/70 border border-slate-600/70 text-slate-100 hover:bg-slate-700/80 hover:text-white'
                } ${submitState.loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {getCategoryLabel(cat)}
              </button>
            ))}
          </div>
        </div>

        {/* Details Input */}
        <div className="space-y-2">
          <label htmlFor="feedback-details" className="text-xs text-slate-200 block drop-shadow-sm bg-slate-900/40 px-2 py-0.5 rounded w-fit">
            Details
          </label>
          <textarea
            id="feedback-details"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            disabled={submitState.loading}
            placeholder="Tell us more..."
            maxLength={8000}
            rows={4}
            className="w-full px-3 py-2 bg-slate-900/80 border border-slate-600/70 rounded-lg text-sm text-white placeholder-slate-300/80 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-[1px]"
          />
          <div className="flex justify-between items-center text-xs text-slate-200/90 drop-shadow-sm bg-slate-900/40 px-2 py-0.5 rounded w-fit">
            <span>{details.length} / 8000</span>
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={submitState.loading || !details.trim()}
          className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-all shadow-md ${
            submitState.loading || !details.trim()
              ? 'bg-slate-800/70 border border-slate-600/70 text-slate-400 cursor-not-allowed'
              : 'bg-cyan-500 hover:bg-cyan-400 text-slate-900 border border-cyan-300/90'
          }`}
        >
          {submitState.loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Sending...
            </span>
          ) : (
            getButtonLabel()
          )}
        </button>

        {/* Success Message */}
        {submitState.success && (
          <div className="p-3 bg-emerald-900/60 border border-emerald-400/80 rounded-lg backdrop-blur-[1px] shadow-inner">
            <p className="text-sm text-emerald-50">✓ Thanks for your feedback!</p>
          </div>
        )}

        {/* Error Message */}
        {submitState.error && (
          <div className="p-3 bg-red-900/70 border border-red-500/80 rounded-lg backdrop-blur-[1px] shadow-inner">
            <p className="text-sm text-red-50">{submitState.error}</p>
          </div>
        )}
      </div>
    </div>
  );
};
